import type { QuestionResultFilter } from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";

export interface QuestionResultLabelParts {
    moduleLabel: string;
    questionLabel: string;
    tagLabels: string[];
}

/**
 * Resolves the display names for a question result selection.
 *
 * Module names come from the filterable module listing, questions and answer
 * tags from the lazily cached module detail. Every lookup falls back to a
 * generic label so a selection still renders when the module list has not
 * loaded yet, or when it references a module the current user cannot access.
 */
export function getQuestionResultLabelParts(
    questionResult: QuestionResultFilter,
    valuesStore: ApplicationFilterValuesStore,
): QuestionResultLabelParts {
    const moduleDetails = valuesStore.moduleDetailsById.get(
        questionResult.licensedModuleId,
    );
    const moduleListing = valuesStore.questionResultFilterableModules.find(
        (module) => module.id === questionResult.licensedModuleId,
    );

    const question = moduleDetails?.questions?.find(
        (item) => item.id === questionResult.questionId,
    );
    const tagsById = new Map((question?.tags ?? []).map((tag) => [tag.id, tag]));

    return {
        moduleLabel:
            moduleListing?.name ?? moduleDetails?.name ?? "Saved Module",
        questionLabel:
            question?.shortQuestionText ?? question?.questionText ?? "Question",
        tagLabels: questionResult.tagIds.map(
            (tagId) => tagsById.get(tagId)?.value ?? "Result",
        ),
    };
}

/**
 * Warms the module list and per-module detail caches so label lookups resolve
 * to real names. Both loaders no-op when already cached or in flight, so this
 * is safe to await on every render path that displays question results.
 */
export async function loadQuestionResultLabelData(
    questionResults: QuestionResultFilter[] | undefined,
    valuesStore: ApplicationFilterValuesStore,
): Promise<void> {
    const moduleIds = Array.from(
        new Set(
            (questionResults ?? [])
                .map((questionResult) => questionResult.licensedModuleId)
                .filter((moduleId) => !!moduleId),
        ),
    );
    if (moduleIds.length === 0) return;

    await Promise.all([
        valuesStore.loadQuestionResultFilterableModules(),
        ...moduleIds.map((moduleId) => valuesStore.loadModuleDetails(moduleId)),
    ]);
}
